import { useState, useEffect, useRef } from "react";
import * as Sentry from "../../lib/errorReporting";
import { apiFetch, authedApiFetch } from "../../lib/api";
import { fmtOdds, classifySlip, calcPayout } from "../../lib/betting";
import { resolveMarket, isPlayerPropMarket, marketFieldText } from "../../lib/markets";
import { SPORTSBOOKS, APP_VERSION, SUPPORTED_LEAGUES } from "../../lib/constants";
import { computeSaveBlockers, gamelessLegMessage, SPREAD_SIGN_UNCONFIRMED_422_MESSAGE, UNKNOWN_SPORTSBOOK_422_MESSAGE, NEGATIVE_LINE_MESSAGE, SAVE_DAILY_LIMIT_MESSAGE, SAVE_LIMIT_UNAVAILABLE_MESSAGE } from "../../lib/save-blockers";
import { applyLineSign, editSpreadLine } from "../../lib/spread-sign";
import { supabase } from "../../lib/supabase";
import { readClipboardImage, createObjectURL, revokeObjectURL, createFileReader, createFile, isCapacitorNative, pickPhotoFromLibrary } from "../../lib/platform";
import { prepareSlipImageForUpload } from "../../lib/image-compression";
import { track } from "../../lib/analytics";
import { JT } from "../../components/ui/jumbotronTokens";
import GamePicker from "../../components/ui/GamePicker";
import OfflineBanner from "../../components/ui/OfflineBanner";
import ImportUploadState from "../../components/ui/ImportUploadState";
import ImportParsingState from "../../components/ui/ImportParsingState";
import ImportErrorState from "../../components/ui/ImportErrorState";
import { sportMismatchCopy } from "../../lib/sport-mismatch-copy";
import ImportConfirmDetails, { EDIT_ACCENT } from "./ImportConfirmDetails";
import LegWarningBadge from "./components/LegWarningBadge";

export default function SlipImporter({games, sportsbook, league = SUPPORTED_LEAGUES[0], onConfirm, user, rosters, rostersLoaded, open, isOnline, onLockChange, isReady, syncFailed, retrySync, refetchBetaStatus}) {
  const [stage, setStage]       = useState("upload");   // upload | parsing | confirm | error
  const [preview, setPreview]   = useState(null);        // base64 image
  const [parsed, setParsed]     = useState(null);        // parsed slip data
  const [feedback, setFeedback] = useState(null);        // "yes" | "no"
  const [debugInfo, setDebugInfo] = useState("");
  const [errorCopy, setErrorCopy] = useState(null); // #1652: a refusal the server explains


  const [originalParsed, setOriginalParsed] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedImageMediaType, setCapturedImageMediaType] = useState(null);

  const [offlineMsg, setOfflineMsg] = useState(false);
  const confirmedWriteRef = useRef(false);
  const cancelledRef = useRef(false);
  const abortControllerRef = useRef(null);
  const parseCompleteRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  // #699 G2: server-returned per-leg market-support warnings, surfaced (non-blocking)
  // after save and acknowledged before completing. The full per-leg badge + UNKNOWN
  // correction/remap flow is a tracked fast-follow.
  const [postSaveWarnings, setPostSaveWarnings] = useState(null);
  const [pendingSavedBet, setPendingSavedBet] = useState(null);
  const [postSaveCompleting, setPostSaveCompleting] = useState(false);
  const completingRef = useRef(false); // synchronous double-tap guard for the Continue handler
  // PostHog import funnel (spec §4 step 11, §6). iOS only (C9): an import starts when the
  // native picker resolves. After import_completed (server persistence) nothing more is
  // emitted for that import; cancels, offline guards and early returns emit nothing.
  const importStartedRef = useRef(false);
  const completedRef = useRef(false);
  const emitImportFailed = (stage, reason, props = {}) => {
    if (importStartedRef.current && !completedRef.current) track('import_failed', { stage, reason_code: reason, ...props });
  };
  // #1166 D2.3 / final review I2: which legs a game choice FILLED, as
  // Map<filledLegIndex, sourceLegIndex>. Held beside `parsed`, never inside a leg,
  // so it can never be serialized into the import-confirm payload or bets.legs.
  // Reset on every new parse. Updates are idempotent (StrictMode double-invokes
  // the setParsed updater that writes it).
  const gameFillsRef = useRef(new Map());
  // #1132 D5.3: the leg indexes the server's last 422 SPREAD_SIGN_UNCONFIRMED listed.
  // Those rows get the sign control even when the client predicted no flag. Cleared on
  // a new parse and on a successful save.
  const [spreadSignFailedIdx, setSpreadSignFailedIdx] = useState(null);

  useEffect(() => {
    if (!offlineMsg) return;
    const t = setTimeout(() => setOfflineMsg(false), 3000);
    return () => clearTimeout(t);
  }, [offlineMsg]);

  useEffect(() => {
    if (!open) {
      cancelledRef.current = true;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
    if (open) { confirmedWriteRef.current = false; return; }
    if (confirmedWriteRef.current) { confirmedWriteRef.current = false; setCapturedImage(null); setCapturedImageMediaType(null); return; }
    if (!open) {
      // Dead End A: user tapped "Something's off" then closed without saving — write negative signal.
      // Best-effort authenticated write. If session is expired at teardown, the 401 is caught below.
      if (feedback === 'no' && stage === 'confirm' && parsed) {
        authedApiFetch("/api/claude-vision", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-sliptrack-app-version": APP_VERSION, "x-sliptrack-capabilities": "league_aware,game_choice" },
          body: JSON.stringify({
            confirmed: true,
            parsedJson: parsed,
            sportsbook: parsed.sportsbook,
            corrected: false,
            userFlagged: true,
            capturedImage,
            league: parsed.league || league || SUPPORTED_LEAGUES[0],
            // Without this the server has no trusted parse to grade against, so
            // EVERY row from this writer is stamped STAGE_C_SOURCE_UNAVAILABLE --
            // which this branch made disqualifying in both promotion evaluators.
            // The whole negative-signal path would have gone permanently
            // corpus-ineligible, silently. The value is already on `parsed`.
            import_log_id: parsed.importLogId ?? null,
          }),
        // response.ok check intentionally omitted --
        // best-effort signal, 401 on expired session is acceptable.
        }).catch((err) => {
          Sentry.captureMessage('Training data write failed', {
            level: 'warning',
            tags: { function: 'negativeTrainingSignal', failureStage: 'negative-feedback-log' },
            extra: { error: err?.message },
          });
        });
      }
      setCapturedImage(null);
      setCapturedImageMediaType(null);
    }
  }, [open]);


  // Revoke preview blob URL when it changes or component unmounts
  useEffect(() => {
    return () => { if (preview) revokeObjectURL(preview); };
  }, [preview]);

  // Sync sheet lock with stage — lock when the confirm screen is showing OR while a
  // post-save market-support warning is awaiting acknowledgment (#699 G2 #4). Binding
  // the lock to postSaveWarnings (not just stage) makes Continue the GUARANTEED sole
  // exit: a BottomSheet backdrop/swipe/✕ dismiss stays inert while a warning is
  // pending, so the already-persisted-but-unacknowledged slip cannot be stranded out
  // of App state (realtime only recovers UPDATE, not this INSERT).
  useEffect(() => {
    if (onLockChange) onLockChange(stage === 'confirm' || !!postSaveWarnings);
  }, [stage, postSaveWarnings]);

  const lbl = {...JT.type.label,fontWeight:800,color:JT.text.tertiary,marginBottom:4,display:"block",letterSpacing:"1px",whiteSpace:"normal"};
  const inp = {width:"100%",background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:9,padding:"11px 13px",color:JT.text.body,fontSize:16,outline:"none",boxSizing:"border-box",fontFamily:"inherit"};
  const ticketNameInput = {width:"100%",background:"transparent",border:"none",borderBottom:`1px dashed ${JT.text.dim}`,outline:"none",padding:"4px 0",color:JT.text.primary,fontSize:14,fontWeight:800,fontFamily:JT.font.display,boxSizing:"border-box"};
  const ticketMarketInput = {width:"100%",background:"transparent",border:"none",borderBottom:`1px dashed ${JT.text.dim}`,outline:"none",padding:"4px 0",color:JT.text.tertiary,fontSize:11,fontFamily:"inherit",boxSizing:"border-box"};
  // Focus affordance for ticket-skinned inputs — resting state has no visible box (outline:"none"
  // above), so on focus we show a subtle ring to signal the field is editable (spec §4.6).
  const focusRing = e => { e.target.style.outline = `1px solid ${JT.border.mid}`; };
  const blurRing = e => { e.target.style.outline = "none"; };

  // Shared parsing pipeline — both web and native paths converge here
  const parseSlipImage = async ({ base64, mediaType, previewUrl }) => {
    setCapturedImage(base64);
    setCapturedImageMediaType(mediaType);
    setPreview(previewUrl);
    setStage("parsing");

    cancelledRef.current = false;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    let visionStatus = null;
    try {
      const response = await authedApiFetch("/api/claude-vision", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-sliptrack-app-version": APP_VERSION, "x-sliptrack-capabilities": "league_aware,game_choice" },
        body: JSON.stringify({
          image: base64,
          mediaType,
          preSelectedBook: sportsbook || "unknown",
          league: league || SUPPORTED_LEAGUES[0],
        }),
        signal: abortControllerRef.current.signal,
      });
      visionStatus = response.status; // before json(): a non-JSON error body throws there

      const data = await response.json();

      // #1652: the screenshot's legs are from another league -- say so, not "try a clearer
      // screenshot" (a retry is another paid parse that refuses the same way).
      const mismatchCopy = !response.ok && data?.code === 'SPORT_MISMATCH' ? sportMismatchCopy(data) : null;
      if (mismatchCopy) {
        emitImportFailed('vision', 'sport_mismatch', { detected_sport: data.sport });
        setErrorCopy(mismatchCopy);
        setDebugInfo("");
        setStage("error");
        return;
      }
      if (!response.ok) {
        emitImportFailed('vision', 'http_error');
        if (response.status === 403 && refetchBetaStatus) refetchBetaStatus();
        setDebugInfo(`API Error: ${data.error}`);
        setStage("error");
        return;
      }

      const text = data.text || "";
      setDebugInfo(text.slice(0, 300));

      // Resilient JSON extraction — find first { ... } block in response
      let result;
      try {
        // Try direct parse first
        const clean = text.replace(/```json|```/g,"").trim();
        result = JSON.parse(clean);
      } catch {
        // Fall back to extracting JSON block from anywhere in the response
        const match = text.match(/\{[\s\S]*\}/);
        if (!match) throw new Error("No JSON found in response: " + text.slice(0,200));
        result = JSON.parse(match[0]);
      }

      // Ensure required fields
      result.id = Date.now()*1000+Math.floor(Math.random()*1000);
      result.settledAt = null;
      if (!result.legs) result.legs = [];

      // User pre-selection wins; fall back to AI-detected book
      const VALID_BOOKS = ["fd","dk","mgm","wh","espn","fan"];
      result.sportsbook = (sportsbook && sportsbook !== "unknown")
        ? sportsbook
        : (VALID_BOOKS.includes(result.detectedBook) ? result.detectedBook : sportsbook);

      // Track 2: carry importLogId from parse response through confirm lifecycle
      result.importLogId = data.importLogId ?? null;

      // Freeze league at parse time — vision may omit it, and the league
      // selector stays interactive on the confirm screen. Without this,
      // confirmAndSave falls back to the live prop, which may have changed.
      result.league = result.league || league;

      gameFillsRef.current = new Map();
      setSpreadSignFailedIdx(null);
      setParsed(result);
      setOriginalParsed(JSON.parse(JSON.stringify(result))); // deep snapshot before any edits
      parseCompleteRef.current = Date.now();
      setStage("confirm");
    } catch(err) {
      if (err.name === 'AbortError') { setStage("upload"); return; }
      let visionReason = 'unknown';
      if (visionStatus >= 400) visionReason = 'http_error';
      else if (err?.name === 'TypeError' && visionStatus === null) visionReason = 'network_error';
      else if (err?.name === 'SyntaxError' || String(err?.message).startsWith('No JSON found')) visionReason = 'parse_failed';
      emitImportFailed('vision', visionReason);
      Sentry.captureException(err);
      console.error("[SlipTrack Import Error]", err.message);
      const isImageError = err.message === "Could not process image";
      setDebugInfo(isImageError
        ? "Could not read the image file. On iPhone, try saving the screenshot to Photos first, then selecting from your library."
        : (prev => prev ? prev + " | JS Error: " + err.message : "JS Error: " + err.message));
      setStage("error");
    }
  };

  const handleImage = async (e) => {
    if (!isOnline) { setOfflineMsg(true); return; }
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Single file — use existing single flow
    if (files.length === 1) {
      const file = files[0];
      const compressed = await prepareSlipImageForUpload(file);
      if (!compressed.base64) {
        Sentry.captureMessage('[SlipImporter] prepareSlipImageForUpload returned empty base64', { level: 'warning', extra: { fileName: file.name, fileSize: file.size } });
        setDebugInfo("Could not process the image. On iPhone, try saving the screenshot to Photos first, then selecting from your library.");
        setStage("error");
        return;
      }
      let previewUrl;
      try {
        previewUrl = createObjectURL(file);
      } catch {
        setDebugInfo("Could not read the image file. On iPhone, try saving the screenshot to Photos first, then selecting from your library.");
        setStage("error");
        return;
      }
      await parseSlipImage({ base64: compressed.base64, mediaType: compressed.mediaType, previewUrl });
    }
  };

  const handleCapacitorPick = async () => {
    if (!isOnline) { setOfflineMsg(true); return; }
    let photoResolved = false; // local: an earlier failed import must not make a pre-resolve throw count
    try {
      const photo = await pickPhotoFromLibrary();
      photoResolved = true;
      importStartedRef.current = true;
      completedRef.current = false;
      track('import_started');
      const dataUrl = photo.dataUrl || '';
      // Extract base64 from data URI (strip "data:image/jpeg;base64," prefix)
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : '';
      // Normalize format to MIME type ('jpg' -> 'jpeg')
      const fmt = (photo.format || 'jpeg').toLowerCase();
      const mediaType = `image/${fmt === 'jpg' ? 'jpeg' : fmt}`;

      if (!base64 || base64.length > 3_750_000) {
        emitImportFailed('pick', 'image_unreadable');
        Sentry.captureException(new Error(`[SlipImporter] Native photo picker base64 issue: ${!base64 ? 'empty' : 'oversized (' + base64.length + ' chars)'}`));
        setDebugInfo("Could not process the image. Please try a different screenshot.");
        setStage("error");
        return;
      }

      await parseSlipImage({ base64, mediaType, previewUrl: dataUrl });
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      // Capacitor cancel — user dismissed the picker
      if (msg.includes('cancel')) {
        return;
      }
      // Permission denied / gallery access failure
      if (msg.includes('denied') || msg.includes('access')) {
        Sentry.captureMessage('[SlipImporter] Photo library access denied', {
          level: 'warning',
          tags: { function: 'handleCapacitorPick' },
          extra: { error: err?.message },
        });
        setDebugInfo("Photo library access denied. Please enable it in Settings.");
        setStage("error");
        return;
      }
      // Unexpected error
      if (photoResolved) emitImportFailed('pick', 'unknown');
      Sentry.captureException(err);
      setDebugInfo("Something went wrong selecting a photo. Please try again.");
      setStage("error");
    }
  };

  const handleFeedback = (wasCorrect) => {
    setFeedback(wasCorrect ? "yes" : "no");
  };

  const handleEditLeg = (legIndex, field, value) => {
    setParsed(prev => {
      if (!prev) return prev;
      const legs = [...(prev.legs || [])];
      const updatedLeg = { ...legs[legIndex], [field]: value };
      if (field === 'playerName') updatedLeg.player = value;
      if (field === 'player') updatedLeg.playerName = value;
      if (field === 'playerName' || field === 'player') {
        const origLeg = (originalParsed?.legs || [])[legIndex];
        const origPlayer = origLeg?.playerName ?? origLeg?.player ?? null;
        if (origPlayer && legs[legIndex].pick === origPlayer) {
          updatedLeg.pick = value;
        }
      }
      if (field === 'type' || field === 'market') { updatedLeg.type = value; updatedLeg.market = value; updatedLeg.canonicalMarket = resolveMarket(value, prev.league || league, prev.sportsbook || sportsbook) || null; }
      // #1132 D5.3: the last explicit sign wins; a pick edit clears the answer.
      if (field === 'lineSign') {
        const sign = value === '+' || value === '-' ? value : undefined;
        if (sign) updatedLeg.lineSign = sign; else delete updatedLeg.lineSign;
        updatedLeg.line = applyLineSign(legs[legIndex].line, sign);
      }
      if (field === 'line') {
        const edit = editSpreadLine(legs[legIndex].lineSign, value);
        updatedLeg.line = edit.line;
        if (edit.lineSign) updatedLeg.lineSign = edit.lineSign;
      }
      if (field === 'pick') delete updatedLeg.lineSign;
      legs[legIndex] = updatedLeg;
      // #1166 D2.3 (Ultron U5): a game choice FILLS other legs on the matchup that have
      // no game yet; it never overwrites a game the user or the server set.
      // Final review I2: a filled leg FOLLOWS its source when the user changes the
      // source's game, until the user edits the filled leg itself (then it is theirs).
      // The old "user-edited" guard (orig gameId != current gameId on a leg with no
      // game) was unreachable -- the picker cannot clear a game -- and is replaced by
      // this tracking: a leg with no game has never been chosen by anyone.
      if (field === 'gameId' && value) {
        const fills = gameFillsRef.current;
        const hasCandidate = (l, id) => Array.isArray(l.gameCandidates) && l.gameCandidates.some((c) => String(c.id) === String(id));
        fills.delete(legIndex); // a direct choice makes this leg user-owned
        const prevGame = String((prev.legs || [])[legIndex]?.gameId ?? '');
        for (const [j, src] of [...fills]) {
          if (src !== legIndex) continue;
          const l = legs[j];
          // Still holds the source's old game and can be on the new one: follow it.
          // Otherwise it keeps its game and is no longer a fill.
          if (l && prevGame && String(l.gameId ?? '') === prevGame && hasCandidate(l, value)) legs[j] = { ...l, gameId: value };
          else fills.delete(j);
        }
        legs.forEach((l, j) => {
          if (j === legIndex || l.gameId) return;
          if (hasCandidate(l, value)) {
            legs[j] = { ...l, gameId: value };
            fills.set(j, legIndex);
          }
        });
      }
      return { ...prev, legs };
    });
  };

  const handleEditSingle = (field, rawValue) => {
    setParsed(prev => {
      if (!prev) return prev;
      // #1132 D5.3 (P2-3): a single grades from its ROOT line, so a sign answer rewrites
      // the root and legs[0] together. lineSign itself lives on legs[0] only.
      if (field === 'lineSign') {
        const sign = rawValue === '+' || rawValue === '-' ? rawValue : undefined;
        if (!prev.legs || prev.legs.length === 0) return prev;
        const leg0 = { ...prev.legs[0] };
        if (sign) leg0.lineSign = sign; else delete leg0.lineSign;
        const shown = prev.line ?? leg0.line;
        const line = applyLineSign(shown, sign);
        const next = { ...prev, legs: [leg0, ...prev.legs.slice(1)] };
        if (line !== shown) { next.line = line; leg0.line = line; }
        return next;
      }
      let value = rawValue;
      let nextLineSign;
      if (field === 'line') {
        const edit = editSpreadLine(prev.legs?.[0]?.lineSign, rawValue);
        value = edit.line;
        nextLineSign = edit.lineSign;
      }
      const updated = { ...prev, [field]: value };
      if (field === 'playerName') updated.player = value;
      if (field === 'player') updated.playerName = value;
      if (field === 'playerName' || field === 'player') {
        const origPlayer = originalParsed?.playerName ?? originalParsed?.player ?? null;
        if (origPlayer && prev.pick === origPlayer) {
          updated.pick = value;
        }
      }
      if (field === 'type' || field === 'market') { updated.type = value; updated.market = value; updated.canonicalMarket = resolveMarket(value, prev.league || league, prev.sportsbook || sportsbook) || null; }
      if (updated.legs && updated.legs.length > 0) {
        const legs = [...updated.legs];
        const updatedLeg = { ...legs[0], [field]: value };
        if (field === 'playerName') updatedLeg.player = value;
        if (field === 'player') updatedLeg.playerName = value;
        if (field === 'playerName' || field === 'player') {
          const origLeg = (originalParsed?.legs || [])[0];
          const origPlayer = origLeg?.playerName ?? origLeg?.player ?? null;
          if (origPlayer && legs[0].pick === origPlayer) {
            updatedLeg.pick = value;
          }
        }
        if (field === 'type' || field === 'market') { updatedLeg.type = value; updatedLeg.market = value; updatedLeg.canonicalMarket = resolveMarket(value, prev.league || league, prev.sportsbook || sportsbook) || null; }
        if (field === 'line' && nextLineSign) updatedLeg.lineSign = nextLineSign;
        if (field === 'pick') delete updatedLeg.lineSign;
        legs[0] = updatedLeg;
        updated.legs = legs;
      }
      return updated;
    });
  };

  const confirmAndSave = async () => {
    if (!isOnline) { setOfflineMsg(true); return; }
    if (!isReady) return;
    if (isSaving) return;
    if (!parsed) return;

    setIsSaving(true);
    setSaveError(null);

    const changedFields = [];
    if (!originalParsed && parsed) {
      Sentry.captureMessage('[SlipImporter] originalParsed missing at confirm', {
        level: 'warning',
        tags: { function: 'confirmAndSave' },
        extra: { sportsbook: parsed.sportsbook, slipType: parsed.slipType },
      });
    }
    if (originalParsed) {
      const topLevel = ["sportsbook","type","market","pick","player","playerName","line","overUnder"];
      topLevel.forEach(f => {
        if (String(originalParsed[f]??'') !== String(parsed[f]??'')) changedFields.push(f);
      });
      const legFields = ["type","market","player","playerName","line","overUnder","pick","gameId"];
      const maxLegs = Math.max((originalParsed.legs||[]).length, (parsed.legs||[]).length);
      for (let i = 0; i < maxLegs; i++) {
        const ol = (originalParsed.legs||[])[i] || {};
        const cl = (parsed.legs||[])[i] || {};
        legFields.forEach(f => {
          if (String(ol[f]??'') !== String(cl[f]??'')) changedFields.push(`legs[${i}].${f}`);
        });
      }
    }

    const wasCorrected = changedFields.length > 0;

    let correctionDiff = null;
    if (wasCorrected && originalParsed) {
      correctionDiff = {};
      changedFields.forEach(fieldPath => {
        const legMatch = fieldPath.match(/^legs\[(\d+)\]\.(.+)$/);
        if (legMatch) {
          const idx = parseInt(legMatch[1], 10);
          const field = legMatch[2];
          correctionDiff[fieldPath] = {
            before: (originalParsed.legs||[])[idx]?.[field] ?? null,
            after: (parsed.legs||[])[idx]?.[field] ?? null,
          };
        } else {
          correctionDiff[fieldPath] = {
            before: originalParsed[fieldPath] ?? null,
            after: parsed[fieldPath] ?? null,
          };
        }
      });
    }

    const classified = parsed.legs?.length > 0
      ? classifySlip(parsed)
      : parsed;

    if (classified.slipType === "single" && !classified.odds && classified.legs?.[0]?.odds) {
      classified.odds = classified.legs[0].odds;
    }

    classified.importLogId = parsed.importLogId ?? null;

    // Phase B: derive league from parsed result, attach to classified
    const resolvedLeague = parsed.league || league;
    classified.league = resolvedLeague;

    const saveController = new AbortController();
    const saveTimeout = setTimeout(() => saveController.abort(), 30_000);

    const now = new Date();
    const confirmedLocalDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    let saveStatus = null;
    let saveFailure = null;
    try {
      const response = await authedApiFetch("/api/import-confirm", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-sliptrack-app-version": APP_VERSION,
          "x-sliptrack-capabilities": "league_aware,game_choice",
        },
        body: JSON.stringify({
          league: resolvedLeague,
          confirmed: true,
          parsedJson: classified,
          sportsbook: classified.sportsbook,
          corrected: wasCorrected,
          originalJson: wasCorrected ? originalParsed : null,
          correctionFields: wasCorrected ? changedFields : [],
          correctionDiff: wasCorrected ? correctionDiff : null,
          userFlagged: feedback === 'no',
          screenshotData: capturedImage,
          screenshotMediaType: capturedImageMediaType,
          import_log_id: parsed.importLogId ?? null,
          confirmDurationMs: parseCompleteRef.current ? Date.now() - parseCompleteRef.current : null,
          confirmedLocalDate,
        }),
        signal: saveController.signal,
      });
      saveStatus = response.status; // before json(): a malformed JSON error body throws there

      let result = {};
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        result = await response.json();
      }

      // #1132 D5.2 (P2-6): the server's spread-sign refusal is a recoverable state, not an
      // error. Flag the rows it lists, show the copy, stop saving; never captureException.
      if (response.status === 422 && result.code === 'SPREAD_SIGN_UNCONFIRMED') {
        const failed = Array.isArray(result.failedLegIndexes) ? result.failedLegIndexes.filter(Number.isInteger) : [];
        emitImportFailed('save', 'server_refused');
        setSpreadSignFailedIdx(failed);
        setSaveError(SPREAD_SIGN_UNCONFIRMED_422_MESSAGE);
        Sentry.captureMessage('[SlipImporter] spread sign unconfirmed by server', {
          level: 'warning',
          tags: { function: 'confirmAndSave', failureStage: 'spread-sign-unconfirmed' },
          extra: { failedLegIndexes: failed, importLogId: parsed.importLogId ?? null },
        });
        return;
      }

      // Spec 2026-09-25 §8: the server's field-authority refusals are recoverable input
      // errors. Client-owned copy keyed on code (never server text, curated-copy rule
      // :586-590); warning, never captureException (CC-IMPL-037).
      const FIELD_REFUSAL_COPY = { UNKNOWN_SPORTSBOOK: UNKNOWN_SPORTSBOOK_422_MESSAGE, NEGATIVE_LINE_UNSIGNED_MARKET: NEGATIVE_LINE_MESSAGE };
      if (response.status === 422 && Object.prototype.hasOwnProperty.call(FIELD_REFUSAL_COPY, result.code)) {
        // Clear a stale spread-sign highlight from an earlier SPREAD_SIGN_UNCONFIRMED
        // 422 -- this refusal is a different field, and its rows must not carry the
        // previous refusal's highlight into this failure state.
        emitImportFailed('save', 'server_refused');
        setSpreadSignFailedIdx(null);
        setSaveError(FIELD_REFUSAL_COPY[result.code]);
        Sentry.captureMessage('[SlipImporter] save refused by server', {
          level: 'warning',
          tags: { function: 'confirmAndSave', failureStage: 'server-field-refused' },
          extra: { code: result.code, failedLegIndexes: Array.isArray(result.failedLegIndexes) ? result.failedLegIndexes : null, importLogId: parsed.importLogId ?? null },
        });
        return;
      }

      // #935: the server's per-user daily cap. Recoverable -- the parsed slip stays on
      // screen; client-owned copy keyed on status + code; warning, never captureException.
      const SAVE_LIMIT_COPY = {
        '429:IMPORT_CONFIRM_DAILY_LIMIT': SAVE_DAILY_LIMIT_MESSAGE,
        '503:IMPORT_CONFIRM_LIMIT_UNAVAILABLE': SAVE_LIMIT_UNAVAILABLE_MESSAGE,
      };
      const SAVE_LIMIT_REASON = {
        '429:IMPORT_CONFIRM_DAILY_LIMIT': 'daily_cap',
        '503:IMPORT_CONFIRM_LIMIT_UNAVAILABLE': 'http_error',
      };
      const limitKey = `${response.status}:${result.code}`;
      if (Object.prototype.hasOwnProperty.call(SAVE_LIMIT_COPY, limitKey)) {
        emitImportFailed('save', SAVE_LIMIT_REASON[limitKey]);
        setSpreadSignFailedIdx(null);
        setSaveError(SAVE_LIMIT_COPY[limitKey]);
        Sentry.captureMessage('[SlipImporter] save limited by server', {
          level: 'warning',
          tags: { function: 'confirmAndSave', failureStage: 'server-save-limited' },
          extra: { code: result.code, status: response.status, importLogId: parsed.importLogId ?? null },
        });
        return;
      }

      if (!response.ok || !result.success) {
        saveFailure = response.ok ? 'server_refused' : 'http_error';
        if (response.status === 403 && refetchBetaStatus) refetchBetaStatus();
        throw new Error(result.error || "Slip failed to save");
      }

      // Server persistence succeeded: the funnel's completion point (§6).
      if (!completedRef.current) {
        completedRef.current = true;
        track('import_completed', { book: classified.sportsbook, league: resolvedLeague, leg_count: classified.legs?.length ?? 0 });
      }

      if (cancelledRef.current) return;

      confirmedWriteRef.current = true;
      setSpreadSignFailedIdx(null);
      setCapturedImage(null);
      setCapturedImageMediaType(null);

      const savedBet = result.bet || {
        ...classified,
        id: result.betId,
        confirmedParseId: result.confirmedParseId ?? null,
        provenanceStatus: result.confirmedParseId ? 'linked' : 'missing',
        _serverPersisted: true,
      };

      savedBet._serverPersisted = true;

      // #699 G2: if the server flagged any leg market as can't-auto-grade
      // (UNSUPPORTED_MARKET_AUTO_GRADE) or unknown (UNKNOWN_MARKET), surface it
      // (non-blocking) and hold completion until the user acknowledges — so the
      // gate's warning is not silently dropped on confirm.
      const legWarnings = Array.isArray(result.legWarnings)
        ? result.legWarnings.filter(w => w && w.warningCode)
        : [];
      if (legWarnings.length > 0) {
        setPendingSavedBet(savedBet);
        setPostSaveWarnings(legWarnings);
        setIsSaving(false);
        return;
      }

      await onConfirm(savedBet);
    } catch (err) {
      const isAbort = err?.name === 'AbortError';
      // No-op after import_completed (e.g. an onConfirm rejection after persistence).
      let saveReason = 'unknown';
      if (saveFailure) saveReason = saveFailure;
      else if (saveStatus >= 400) saveReason = 'http_error';
      else if (isAbort) saveReason = 'timeout';
      else if (err?.name === 'TypeError' && saveStatus === null) saveReason = 'network_error';
      emitImportFailed('save', saveReason);
      // captureException (not captureMessage) even for the abort timeout: unlike the
      // vision-parse path -- where an AbortError is a deliberate user cancellation
      // (sheet close / re-parse) and is intentionally not reported -- a save-timeout
      // abort is a terminal failure of this attempt (no automatic retry; the user
      // must manually retry), so per CC-IMPL-037 it is reported as an exception.
      Sentry.captureException(err, {
        tags: { function: 'confirmAndSave', failureStage: isAbort ? 'import-confirm-save-timeout' : 'import-confirm-save' },
        extra: {
          importLogId: parsed.importLogId ?? null,
          sportsbook: parsed.sportsbook ?? null,
        },
      });
      // Curated, non-sensitive copy only. Never surface raw err.message: on the
      // generic path err.message can carry raw server/PostgREST text (table names,
      // PG error codes, constraint fragments) or a post-persist onConfirm/JSON
      // error. The timeout string is the one specific message we intentionally show.
      setSaveError(isAbort ? "Save timed out — please try again" : "Slip failed to save. Please try again.");
    } finally {
      clearTimeout(saveTimeout);
      setIsSaving(false);
    }
  };

  // handlePaste — kept for Capacitor sprint rewire
  const handlePaste = async () => {
    if (!isOnline) { setOfflineMsg(true); return; }
    try {
      const items = await readClipboardImage();
      for (const item of items) {
        const imageType = item.types.find(t => t.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = createFile([blob], "pasted-slip.png", {type: imageType});
          const fakeEvent = {target: {files: [file]}};
          handleImage(fakeEvent);
          return;
        }
      }
    } catch(err) {
      Sentry.captureException(err);
      setStage("error");
    }
  };

  // ── UPLOAD STAGE ──
  if (stage==="upload") return (
    <div>
      <OfflineBanner visible={offlineMsg} onDismiss={()=>setOfflineMsg(false)}/>
      <ImportUploadState
        isNative={isCapacitorNative()}
        onFileChange={handleImage}
        onNativePick={handleCapacitorPick}
      />
      <div style={{fontSize:11,color:JT.text.tertiary,textAlign:"center",lineHeight:1.6}}>
        Confirmed imports help SlipTrack's parser get smarter. Your screenshot is securely stored to improve accuracy for every user.
      </div>
    </div>
  );

  // ── PARSING STAGE ──
  if (stage==="parsing") return (
    <ImportParsingState total={1} preview={preview}/>
  );

  // ── ERROR STAGE ──
  if (stage==="error") return (
    <ImportErrorState debugInfo={debugInfo} {...(errorCopy || {})} onRetry={()=>{setErrorCopy(null);setStage("upload");setPreview(null);setCapturedImage(null);setCapturedImageMediaType(null);}}/>
  );

  // ── CONFIRM STAGE ──
  if (stage==="confirm"&&parsed) {
  const editedFields = new Set();
  if (originalParsed) {
    const topFields = ["sportsbook","type","market","pick","player","playerName","line","overUnder"];
    topFields.forEach(f => {
      if (String(originalParsed[f]??'') !== String(parsed[f]??'')) editedFields.add(f);
    });
    // #1132 D5.3: lineSign marks the row edited, but is NOT in changedFields (not a correction).
    const legFlds = ["type","market","player","playerName","line","overUnder","pick","gameId","lineSign"];
    const maxL = Math.max((originalParsed.legs||[]).length, (parsed.legs||[]).length);
    for (let i = 0; i < maxL; i++) {
      const ol = (originalParsed.legs||[])[i] || {};
      const cl = (parsed.legs||[])[i] || {};
      legFlds.forEach(f => {
        if (String(ol[f]??'') !== String(cl[f]??'')) editedFields.add(`legs[${i}].${f}`);
      });
    }
  }
  const blockers = computeSaveBlockers(parsed, { originalParsed, failedLegIndexes: spreadSignFailedIdx });
  const softWarnMessages = blockers.softWarnMessages || [];
  const spreadSignLegIndices = blockers.spreadSignLegIndices || [];
  const legMessages = blockers.legMessages || {};
  const missingStake = !parsed.stake;
  const missingOdds = !(parsed.combinedOdds || parsed.odds);
  const canSave = blockers.canSave && !missingStake && !missingOdds;
  const combinedOddsDisplay = parsed.combinedOdds || fmtOdds(parsed.odds);
  const stakeNumForPayout = Number(parsed.stake);
  const oddsForPayout = parsed.combinedOdds ?? parsed.odds;
  const hasValidStake = !!parsed.stake && !isNaN(stakeNumForPayout);
  const hasValidOdds = !!(parsed.combinedOdds || parsed.odds);
  const toWinDisplay = (hasValidStake && hasValidOdds) ? `$${calcPayout(stakeNumForPayout, oddsForPayout)}` : "—";
  const legCount = parsed.legs?.length || (parsed.slipType === 'single' ? 1 : 0);
  const showTypeChip = legCount > 1;
  const typeChipLabel = (parsed.slipType === 'sgp' || parsed.slipType === 'sgp+') ? 'SAME GAME PARLAY' : 'PARLAY';
  return (
    <div>
      <OfflineBanner visible={offlineMsg} onDismiss={()=>setOfflineMsg(false)}/>

      <button disabled={!!postSaveWarnings} onClick={()=>{ if (postSaveWarnings) return; cancelledRef.current=true;setStage("upload");setPreview(null);setParsed(null);setFeedback(null);setCapturedImage(null);setCapturedImageMediaType(null);setDebugInfo("");}}
        style={{marginBottom:14,padding:"8px 10px",background:JT.bg.panel,border:`1px solid ${JT.border.mid}`,borderRadius:4,color:JT.text.tertiary,fontSize:11,fontWeight:800,letterSpacing:"1.5px",cursor:postSaveWarnings?"not-allowed":"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",gap:4,opacity:postSaveWarnings?0.5:1}}>
        ‹ CANCEL
      </button>

      {preview&&<img src={preview} alt="slip" style={{width:"100%",maxHeight:180,objectFit:"contain",borderRadius:12,marginBottom:14,border:`1px solid ${JT.border.default}`}}/>}

      <div style={{...JT.display900,fontSize:36,color:JT.text.primary,lineHeight:1}}>IMPORT SLIP</div>
      <div style={{...JT.type.label,fontWeight:800,letterSpacing:"2px",color:JT.text.tertiary,margin:"6px 0 16px"}}>AI PARSED · REVIEW &amp; CONFIRM</div>

      {/* Chips row — sportsbook (or fallback picker) + parlay/SGP type */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
        {parsed.sportsbook ? (() => {
          const book = SPORTSBOOKS.find(s=>s.id===parsed.sportsbook);
          const bookColor = JT.book[parsed.sportsbook] || book?.color || JT.text.secondary;
          const bookBg = book?.bg || JT.bg.tile;
          return (
            <div style={{padding:"4px 8px",borderRadius:4,background:bookBg,border:`1px solid ${bookColor}59`,color:bookColor,fontSize:11,fontWeight:800}}>
              {book?.name||parsed.sportsbook}
            </div>
          );
        })() : (
          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
            {/* #1506: offer only active books, as SportsbookSelector does (#1505); a detected book still shows as the chip above */}
            {SPORTSBOOKS.filter(s=>s.active).map(s=>{
              const bookColor = JT.book[s.id] || s.color;
              return (
                <button key={s.id} onClick={()=>setParsed(p=>({...p,sportsbook:s.id}))}
                  style={{padding:"4px 8px",borderRadius:4,border:`1px solid ${bookColor}66`,background:s.bg,color:bookColor,fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                  {s.name}
                </button>
              );
            })}
          </div>
        )}
        {showTypeChip && (
          <div style={{padding:"4px 8px",borderRadius:4,background:JT.brand.chipBg,border:`1px solid ${JT.border.chip}`,color:JT.brand.accent,fontSize:11,fontWeight:800}}>
            {typeChipLabel}
          </div>
        )}
      </div>

      {/* Sportsbook required warning */}
      {!parsed.sportsbook && (
        <div style={{fontSize:11,color:JT.status.missing.color,marginBottom:10,padding:"6px 10px",background:JT.status.missing.dim,borderRadius:6,border:`1px solid ${JT.status.missing.border}`}}>
          ⚠️ We couldn't detect your sportsbook — tap one above to continue.
        </div>
      )}

      {/* Server-detected sportsbook mismatch — non-blocking warning */}
      {parsed.warningCodes?.includes('SPORTSBOOK_MISMATCH') && parsed.sportsbook && (
        <div style={{fontSize:11,color:JT.status.missing.color,marginBottom:10,padding:"6px 10px",background:JT.status.missing.dim,borderRadius:6,border:`1px solid ${JT.status.missing.border}`}}>
          Detected sportsbook doesn't match your selection — double-check above.
        </div>
      )}

      {/* Game meta — single-bet only; parlay/SGP legs carry their own teams line */}
      {parsed.slipType==="single" && parsed.teams && (
        <div style={{fontSize:11,fontWeight:700,letterSpacing:".5px",color:JT.text.tertiary,marginBottom:12}}>{parsed.teams}</div>
      )}

      <div style={{background:JT.bg.card,borderRadius:JT.radius.card,border:`1px solid ${JT.border.default}`,padding:"2px 14px 14px",marginBottom:14}}>

        {/* Single bet details */}
        {parsed.slipType==="single"&&(
          <>
            {parsed.confidenceScore==='HIGH'&&(
              <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 10px",background:JT.status.won.dim,border:`1px solid ${JT.status.won.border}`,borderRadius:20,marginTop:10,marginBottom:10}}>
                <span style={{fontSize:11,fontWeight:700,color:JT.status.won.color}}>High confidence</span>
                <span style={{fontSize:11,color:JT.text.secondary}}>This bet will auto-track and settle</span>
              </div>
            )}
            {parsed.confidenceScore==='MEDIUM'&&(
              <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 10px",background:JT.status.missing.dim,border:`1px solid ${JT.status.missing.border}`,borderRadius:20,marginTop:10,marginBottom:10}}>
                <span style={{fontSize:11,fontWeight:700,color:JT.status.missing.color}}>Review flagged fields</span>
                <span style={{fontSize:11,color:JT.text.secondary}}>Some details flagged — review before saving</span>
              </div>
            )}
            {parsed.confidenceScore==='LOW'&&(
              <div style={{padding:"10px 12px",background:JT.status.live.dim,border:`1px solid ${JT.status.live.border}`,borderRadius:8,marginTop:10,marginBottom:10,fontSize:12,color:JT.text.secondary,lineHeight:1.5}}>
                This bet may need manual settlement — we'll try to auto-track
              </div>
            )}
            {(parsed.player || parsed.playerName || parsed.legs?.[0]?.playerName || isPlayerPropMarket(parsed.legs?.[0] || parsed)) && (
              <div style={{padding:"12px 0",borderBottom:`1px solid ${JT.border.default}`,borderLeft:(editedFields.has('playerName')||editedFields.has('player'))?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:(editedFields.has('playerName')||editedFields.has('player'))?8:0}}>
                <div style={lbl}>Player {(editedFields.has('playerName')||editedFields.has('player'))&&<span style={{color:EDIT_ACCENT,fontSize:11,fontWeight:600}}>edited</span>}</div>
                <input value={parsed.playerName??parsed.player??parsed.legs?.[0]?.playerName??''} onChange={e=>handleEditSingle('playerName',e.target.value)} onFocus={focusRing} onBlur={blurRing} style={ticketNameInput}/>
              </div>
            )}
            <div style={{display:"flex",justifyContent:"space-between",padding:"12px 0",borderBottom:`1px solid ${JT.border.default}`}}>
              <div style={{flex:1,borderLeft:editedFields.has('pick')?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:editedFields.has('pick')?8:0}}>
                <div style={lbl}>Pick {editedFields.has('pick')&&<span style={{color:EDIT_ACCENT,fontSize:11,fontWeight:600}}>edited</span>}</div>
                <input value={parsed.pick??''} onChange={e=>handleEditSingle('pick',e.target.value)} onFocus={focusRing} onBlur={blurRing} style={ticketNameInput}/>
              </div>
              <div style={{textAlign:"right",marginLeft:10}}>
                <div style={lbl}>Odds</div>
                <div style={{...JT.display800,fontSize:18,color:JT.text.secondary}}>{fmtOdds(parsed.odds)}</div>
              </div>
            </div>
            <div style={{padding:"12px 0",borderBottom:`1px solid ${JT.border.default}`,borderLeft:(editedFields.has('type')||editedFields.has('market'))?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:(editedFields.has('type')||editedFields.has('market'))?8:0}}>
              <div style={lbl}>Market {(editedFields.has('type')||editedFields.has('market'))&&<span style={{color:EDIT_ACCENT,fontSize:11,fontWeight:600}}>edited</span>}</div>
              <input value={marketFieldText(parsed.type??parsed.market??parsed.legs?.[0]?.type)} onChange={e=>handleEditSingle('type',e.target.value)} onFocus={focusRing} onBlur={blurRing} style={ticketMarketInput}/>
            </div>
            <div style={{display:'flex',gap:10,padding:"12px 0"}}>
              <div style={{flex:1,borderLeft:editedFields.has('line')?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:editedFields.has('line')?8:0}}>
                <div style={lbl}>Line {(editedFields.has('line')||editedFields.has('legs[0].lineSign'))&&<span style={{color:EDIT_ACCENT,fontSize:11,fontWeight:600}}>edited</span>}</div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  {spreadSignLegIndices.includes(0)&&(
                    <select aria-label="Spread line sign" value={parsed.legs?.[0]?.lineSign??''} onChange={e=>handleEditSingle('lineSign',e.target.value)} onFocus={focusRing} onBlur={blurRing} style={{...ticketMarketInput,width:60,appearance:'auto'}}>
                      <option value="">Sign</option>
                      <option value="-">-</option>
                      <option value="+">+</option>
                    </select>
                  )}
                  <input type="number" value={parsed.line??parsed.legs?.[0]?.line??''} onChange={e=>handleEditSingle('line',e.target.value===''?null:Number(e.target.value))} onFocus={focusRing} onBlur={blurRing} style={ticketMarketInput}/>
                </div>
              </div>
              <div style={{flex:1,borderLeft:editedFields.has('overUnder')?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:editedFields.has('overUnder')?8:0}}>
                <div style={lbl}>Over/Under {editedFields.has('overUnder')&&<span style={{color:EDIT_ACCENT,fontSize:11,fontWeight:600}}>edited</span>}</div>
                <select value={parsed.overUnder??parsed.legs?.[0]?.overUnder??''} onChange={e=>handleEditSingle('overUnder',e.target.value||null)} onFocus={focusRing} onBlur={blurRing} style={{...ticketMarketInput,appearance:'auto'}}>
                  <option value="">—</option>
                  <option value="Over">Over</option>
                  <option value="Under">Under</option>
                </select>
              </div>
            </div>
            <GamePicker candidates={parsed.legs?.[0]?.gameCandidates} value={parsed.legs?.[0]?.gameId} onChange={(id) => handleEditLeg(0, 'gameId', id)} edited={editedFields.has('legs[0].gameId')} />
            {blockers.affectedLegIndices.includes(0)&&(
              <div style={{fontSize:11,color:JT.text.secondary,marginTop:8,padding:"4px 8px",background:JT.bg.tile,borderRadius:6,border:`1px solid ${JT.border.default}`}}>
                {!(parsed.legs&&parsed.legs[0]&&parsed.legs[0].gameId)
                  ? gamelessLegMessage(parsed.legs[0]?.resolution_failure_reason)
                  : (legMessages[0] || 'Review flagged fields before saving')}
              </div>
            )}
          </>
        )}

        {/* Parlay / SGP legs */}
        {(parsed.slipType==="parlay"||parsed.slipType==="sgp"||parsed.slipType==="sgp+")&&<ImportConfirmDetails legs={parsed.legs} confidenceScore={parsed.confidenceScore} warningCodes={parsed.warningCodes} affectedLegIndices={blockers.affectedLegIndices} spreadSignLegIndices={spreadSignLegIndices} legMessages={legMessages} onEditLeg={handleEditLeg} editedFields={editedFields} />}

        {/* Perforated divider */}
        <div style={{marginTop:16}}>
          <div data-testid="perforated-divider" style={{borderTop:`1.5px dashed ${JT.border.mid}`,margin:"0 -14px",position:"relative"}}>
            <div style={{position:"absolute",top:-8,left:-8,width:16,height:16,borderRadius:"50%",background:JT.bg.root}}/>
            <div style={{position:"absolute",top:-8,right:-8,width:16,height:16,borderRadius:"50%",background:JT.bg.root}}/>
          </div>
        </div>

        {/* Totals — COMBINED / STAKE / TO WIN */}
        <div style={{display:"flex",alignItems:"flex-end",gap:10,marginTop:14}}>
          <div style={{flex:1}}>
            <div style={lbl}>Combined {missingOdds&&<span style={{color:EDIT_ACCENT}}>⚠ required</span>}</div>
            {!missingOdds ? (
              <div style={{...JT.display900,fontSize:30,color:JT.text.primary}}>{combinedOddsDisplay}</div>
            ) : (
              <input
                type="text"
                placeholder="+350…"
                style={{width:"100%",background:"transparent",border:"none",borderBottom:`1px dashed ${EDIT_ACCENT}99`,outline:"none",padding:"2px 0",color:JT.text.primary,fontSize:16,boxSizing:"border-box",fontFamily:"inherit"}}
                onFocus={focusRing} onBlur={blurRing}
                onChange={e=>{
                  const v = e.target.value;
                  setParsed(p=>{
                    const isSingle = p.slipType === "single";
                    const rootOdds = isSingle ? Number(v) : null;
                    return {
                      ...p,
                      combinedOdds: !isSingle ? v : null,
                      odds: rootOdds,
                      // Keep the promoted root value and the leg it was promoted
                      // FROM in sync. normalizeVisionEvidence promotes
                      // legs[0].odds to the root for singles, so writing only the
                      // root left the confirmed artifact self-contradictory
                      // (root -150, legs[0].odds null) — and that artifact is
                      // exported verbatim as a fixture's expected_parsed_json,
                      // i.e. model ground truth. Every per-leg consumer also saw
                      // nothing. Audit revision 4.
                      legs: (isSingle && Array.isArray(p.legs) && p.legs.length > 0)
                        ? [{ ...p.legs[0], odds: rootOdds }, ...p.legs.slice(1)]
                        : p.legs,
                    };
                  });
                }}
              />
            )}
          </div>
          <div style={{flex:1}}>
            <div style={lbl}>Stake {!parsed.stake&&<span style={{color:EDIT_ACCENT}}>⚠ required</span>}</div>
            {parsed.stake ? (
              <div style={{fontSize:14,fontWeight:800,color:JT.text.secondary}}>${parsed.stake}</div>
            ) : (
              <input
                type="number"
                placeholder="Enter stake…"
                style={{width:"100%",background:"transparent",border:"none",borderBottom:`1px dashed ${EDIT_ACCENT}99`,outline:"none",padding:"2px 0",color:JT.text.primary,fontSize:16,fontWeight:800,boxSizing:"border-box",fontFamily:"inherit"}}
                onFocus={focusRing} onBlur={blurRing}
                onChange={e=>setParsed(p=>({...p,stake:Number(e.target.value)||null}))}
              />
            )}
          </div>
          <div style={{flex:1,textAlign:"right"}}>
            <div style={lbl}>To Win</div>
            <div style={{fontSize:14,fontWeight:800,color:JT.status.won.color}}>{toWinDisplay}</div>
          </div>
        </div>

        {/* Missing fields summary */}
        {(!parsed.stake || !(parsed.combinedOdds||parsed.odds)) && (
          <div style={{marginTop:10,padding:"8px 12px",background:JT.status.missing.dim,borderRadius:8,border:`1px solid ${JT.status.missing.border}`,fontSize:11,color:JT.status.missing.color,lineHeight:1.6}}>
            ⚠ Fill in the highlighted fields above before saving.
          </div>
        )}
      </div>

      {/* Accuracy feedback */}
      {feedback===null ? (
        <div style={{background:JT.bg.panel,borderRadius:12,padding:"14px 16px",marginBottom:14,border:`1px solid ${JT.border.hero}`}}>
          <div style={{fontSize:12,color:JT.text.secondary,marginBottom:10,fontWeight:600}}>Did we get this right?</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>handleFeedback(true)} style={{flex:1,padding:"10px",background:JT.status.won.dim,border:`1px solid ${JT.status.won.border}`,borderRadius:9,color:JT.status.won.color,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✓ Looks correct</button>
            <button onClick={()=>handleFeedback(false)} style={{flex:1,padding:"10px",background:JT.status.lost.dim,border:`1px solid ${JT.status.lost.border}`,borderRadius:9,color:JT.status.lost.color,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✗ Something's off</button>
          </div>
        </div>
      ) : feedback==="no" ? (
        <div style={{background:JT.bg.panel,borderRadius:12,padding:"12px 16px",marginBottom:14,border:`1px solid ${JT.status.lost.border}`,fontSize:12,color:JT.status.lost.color}}>
          Thanks for the feedback — this helps us improve accuracy. You can still save and edit the slip manually.
          <button onClick={()=>setFeedback(null)} style={{display:"block",marginTop:8,padding:0,background:"none",border:"none",color:JT.text.secondary,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>↩ Change my answer</button>
        </div>
      ) : (
        <div style={{background:JT.status.won.dim,borderRadius:12,padding:"12px 16px",marginBottom:14,border:`1px solid ${JT.status.won.border}`,fontSize:12,color:JT.status.won.color}}>
          ✓ Great — import accuracy logged.
          <button onClick={()=>setFeedback(null)} style={{display:"block",marginTop:8,padding:0,background:"none",border:"none",color:JT.text.secondary,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>↩ Change my answer</button>
        </div>
      )}

      {!blockers.canSave && (
        <div style={{marginBottom:10,padding:"10px 12px",background:JT.status.lost.dim,borderRadius:8,border:`1px solid ${JT.status.lost.border}`,fontSize:12,color:JT.status.lost.color,lineHeight:1.6}}>
          {blockers.hardBlockMessages.map((msg, i) => (
            <div key={i} style={{marginBottom:i < blockers.hardBlockMessages.length - 1 ? 4 : 0}}>⛔ {msg}</div>
          ))}
        </div>
      )}
      {blockers.canSave && softWarnMessages.length > 0 && (
        <div style={{marginBottom:10,padding:"10px 12px",background:JT.status.missing.dim,borderRadius:8,border:`1px solid ${JT.status.missing.border}`,fontSize:12,color:JT.status.missing.color,lineHeight:1.6}}>
          {softWarnMessages.map((msg, i) => (
            <div key={i} style={{marginBottom:i < softWarnMessages.length - 1 ? 4 : 0}}>⚠ {msg}</div>
          ))}
        </div>
      )}
      {saveError && (
        <div style={{
          marginBottom:10,
          padding:"10px 12px",
          background:JT.status.lost.dim,
          borderRadius:8,
          border:`1px solid ${JT.status.lost.border}`,
          fontSize:12,
          color:JT.status.lost.color,
          lineHeight:1.6
        }}>
          {saveError}
        </div>
      )}
      {postSaveWarnings ? (
        <>
          <div data-testid="leg-warnings-banner" style={{fontSize:11,color:JT.status.missing.color,marginBottom:12,padding:"8px 12px",background:JT.status.missing.dim,borderRadius:8,border:`1px solid ${JT.status.missing.border}`,lineHeight:1.5}}>
            ⚠️ Saved. {postSaveWarnings.length === 1 ? "This market can't" : "These markets can't"} be auto-graded yet — you'll track {postSaveWarnings.length === 1 ? 'it' : 'them'} manually:
            <ul style={{margin:"6px 0 0",paddingLeft:16}}>
              {postSaveWarnings.map((w, i) => (
                <li key={i} style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                  <span>{w.market || 'unknown market'}</span>
                  <LegWarningBadge warningCode={w.warningCode} />
                </li>
              ))}
            </ul>
          </div>
          <button disabled={postSaveCompleting} onClick={async () => {
              // #699 G2 (Codex P2): synchronous re-entry guard so a rapid double-tap
              // cannot fire onConfirm twice (which would prepend a duplicate slip);
              // and mirror the save path's error handling so an onConfirm rejection is
              // captured + surfaced instead of stranding the sheet. The bet is already
              // persisted (confirmedWriteRef), so on error we keep the banner + Continue
              // for a safe retry (onConfirm/onAdd did not complete).
              if (completingRef.current) return;
              completingRef.current = true;
              setPostSaveCompleting(true);
              const bet = pendingSavedBet;
              try {
                if (bet) await onConfirm(bet);
                setPostSaveWarnings(null);
                setPendingSavedBet(null);
              } catch (err) {
                completingRef.current = false;
                setPostSaveCompleting(false);
                Sentry.captureException(err, { tags: { function: 'confirmAndSave', failureStage: 'post-save-warnings-confirm' } });
                setSaveError('Could not finish saving. Please try again.');
              }
            }}
            style={{width:"100%",padding:"16px",background:JT.brand.cta,border:"none",borderRadius:JT.radius.pill,color:JT.text.primary,fontSize:14,fontWeight:800,letterSpacing:".3px",cursor:postSaveCompleting?"not-allowed":"pointer",fontFamily:"inherit",boxShadow:JT.brand.ctaShadow,opacity:postSaveCompleting?0.7:1}}>
            {postSaveCompleting ? 'Saving...' : 'Continue →'}
          </button>
        </>
      ) : (
        <button onClick={confirmAndSave} disabled={!canSave || isSaving || !isReady}
          style={{width:"100%",padding:"16px",background:(canSave && !isSaving && isReady)?JT.brand.cta:JT.border.mid,border:"none",borderRadius:JT.radius.pill,color:(canSave && !isSaving && isReady)?JT.text.primary:JT.text.tertiary,fontSize:14,fontWeight:800,letterSpacing:".3px",cursor:(canSave && !isSaving && isReady)?"pointer":"not-allowed",fontFamily:"inherit",boxShadow:(canSave && !isSaving && isReady)?JT.brand.ctaShadow:"none"}}>
          {isSaving ? 'Saving...' : !isReady ? 'Syncing...' : 'Save Slip →'}
        </button>
      )}
      {!isReady && !isSaving && (
        <div style={{fontSize:11,color:syncFailed?JT.status.lost.color:JT.text.tertiary,textAlign:"center",marginTop:6}}>
          {syncFailed ? 'Could not load your slips.' : 'Loading your slips, please wait...'}
        </div>
      )}
      {syncFailed && !isReady && !isSaving && retrySync && (
        <button onClick={async ()=>{ setIsRetrying(true); await retrySync(); setIsRetrying(false); }} disabled={isRetrying}
          style={{width:"100%",marginTop:8,padding:"10px",background:"none",border:`1px solid ${JT.status.lost.border}`,borderRadius:10,color:isRetrying?JT.text.tertiary:JT.status.lost.color,fontSize:12,fontWeight:600,cursor:isRetrying?"not-allowed":"pointer",fontFamily:"inherit"}}>
          {isRetrying ? 'Retrying...' : 'Retry'}
        </button>
      )}

      <div style={{fontSize:11,fontWeight:700,letterSpacing:".5px",color:JT.text.tertiary,textAlign:"center",marginTop:12}}>
        SlipTrack tracks — it never places bets.
      </div>

      <button disabled={!!postSaveWarnings} onClick={()=>{ if (postSaveWarnings) return; cancelledRef.current=true;setStage("upload");setPreview(null);setParsed(null);setFeedback(null);setCapturedImage(null);setCapturedImageMediaType(null);}} style={{width:"100%",marginTop:8,padding:"12px",background:"none",border:`1px solid ${JT.border.default}`,borderRadius:12,color:JT.text.tertiary,fontSize:13,fontWeight:600,cursor:postSaveWarnings?"not-allowed":"pointer",fontFamily:"inherit",opacity:postSaveWarnings?0.5:1}}>
        ← Try Different Screenshot
      </button>
    </div>
  );
  }

  return null;
}
